import * as Api from '@ViewModels';
import * as React from 'react';
import { useStreamingResponseMutation } from '../../../../../hooks/useStreamingResponseMutation';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { useAIAssistantCreateConversationMutation } from '../../../../../queries/AI/useAIAssistantCreateConversationMutation';
import { useAIAgentTemplateQuery } from '../../../../../queries/AIAgentTemplate/useAIAgentTemplateQuery';
import { getConversationChatMessageQueryKey } from '../../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { reactQueryClient } from '../../../../../web/ReactQueryProvider';
import { AIDA_ASSISTANT_NAME } from '../../../../constants/systemMessages';
import { AnalysisType, CallAnalysisData } from '../types';
import { extractCallContext, formatCallContextForAI } from '../utils/extractCallContext';
import { getAssistantNameFromConversationType } from '../utils/getAssistantNameFromConversationType';

interface UseMultiCallAnalysisProps {
	selectedCalls: CallAnalysisData[];
	companyId?: string;
	leadServedId?: string;
	source?: string;
	agentTemplateId?: string | null;
}

export const useMultiCallAnalysis = ({
	selectedCalls,
	companyId,
	leadServedId,
	source,
	agentTemplateId,
}: UseMultiCallAnalysisProps) => {
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('useMultiCallAnalysis');
	const userSession = useUserSession();

	// Fetch the agent template if an ID is provided
	const agentTemplateQuery = useAIAgentTemplateQuery({
		id: agentTemplateId || '',
		enabled: !!agentTemplateId,
	});

	// Derive assistant name from template's conversation type, or use default
	const assistantName = React.useMemo(() => {
		if (agentTemplateQuery.data?.conversationType) {
			return getAssistantNameFromConversationType(agentTemplateQuery.data.conversationType);
		}
		return AIDA_ASSISTANT_NAME;
	}, [agentTemplateQuery.data?.conversationType]);

	const [selectedAnalysisType, setSelectedAnalysisType] = React.useState<AnalysisType | null>(null);
	const [customSystemMessage, setCustomSystemMessage] = React.useState<string>('');
	const [isAnalysisModalOpen, setIsAnalysisModalOpen] = React.useState(false);
	const [aiResponse, setAiResponse] = React.useState('');
	const [conversationId, setConversationId] = React.useState<string>(null);
	const [hasFetchedFeedback, setHasFetchedFeedback] = React.useState<boolean>(false);
	const [combinedTranscriptionText, setCombinedTranscriptionText] = React.useState<string>('');
	const [hasStartedAnalysis, setHasStartedAnalysis] = React.useState<boolean>(false);
	const [isLoadingTranscriptions, setIsLoadingTranscriptions] = React.useState<boolean>(false);

	const [conversationMessages, setConversationMessages] = React.useState<
		{ role: 'user' | 'assistant'; content: string }[]
	>([]);
	const [isNewAnalysisSession, setIsNewAnalysisSession] = React.useState<boolean>(true);

	// Fetch all transcriptions for selected calls (only when starting analysis)
	const fetchAllTranscriptions = React.useCallback(async () => {
		if (selectedCalls.length === 0) return;

		setIsLoadingTranscriptions(true);
		try {
			const transcriptionPromises = selectedCalls.map(async call => {
				if (!call.phoneCall.transcriptionId) return '';

				try {
					// Use the same API pattern as useGetTranscriptionSpeakerLineViewQuery
					const response = await userSession.webServiceHelper.callAsync<any>(
						Api.ImpersonationBroker.composeApiUrl({
							queryParams: { type: 'SpeakerLine' },
							urlPath: `transcription/${call.phoneCall.transcriptionId}/view`,
						}),
						'GET'
					);

					if (!response?.speakerLines) return '';

					// Format the transcription text from speaker lines (same as CallAnalysisRow)
					const formattedText = response.speakerLines.map((line: any) => `${line.speaker}: ${line.line}`).join('\n');

					const contactName = call.contact ? `${call.contact.firstName} ${call.contact.lastName}` : 'Unknown Contact';
					const companyName = call.company?.companyName || 'Unknown Company';

					// Extract and format call context
					const contextInfo = extractCallContext(call);
					const contextText = formatCallContextForAI(contextInfo);

					return `Call with ${contactName} at ${companyName}:\n\nCall Context:\n${contextText}\n\nTranscription:\n${formattedText}`;
				} catch (error) {
					console.error(`Failed to fetch transcription for call ${call.phoneCall.id}:`, error);
					return '';
				}
			});

			const transcriptions = await Promise.all(transcriptionPromises);
			const validTranscriptions = transcriptions.filter((t: string) => t.length > 0);
			const combined = validTranscriptions.join('\n\n-------end call--------\n\n');
			setCombinedTranscriptionText(combined);
		} catch (error) {
			console.error('Failed to fetch transcriptions:', error);
			errorMessages.pushApiError(error);
		} finally {
			setIsLoadingTranscriptions(false);
		}
	}, [selectedCalls, userSession, errorMessages]);

	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: (error: any) => {
			errorMessages.pushApiError(error);
			logger.logApiError('aiAssistantCreateConversationMutation', error);
		},
		onSuccess: (conversation: any) => {
			setConversationId(conversation.id);
		},
	});

	const aiPromptMutation = useStreamingResponseMutation({
		mutationFn: ({
			assistantName: assistantNameParam,
			convoId,
			chatRequest,
		}: {
			assistantName: string;
			convoId: string;
			chatRequest: Api.AIMessageRequest;
		}) =>
			new Promise<Response>((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${assistantNameParam}/conversation/${convoId ?? conversationId}/stream`,
					}),
					'POST',
					chatRequest,
					resolve,
					reject
				);
			}),
		onEvent: (event: any, _params: any) => {
			const data = JSON.parse(event.data);
			if (event.event !== 'Response') {
				return;
			}

			const messageText = data?.message?.text;

			if (messageText) {
				if (data.isDelta === false) {
					setAiResponse(data.message.text);
				} else {
					setAiResponse(prev => {
						return prev + messageText;
					});
				}

				reactQueryClient.setQueryData(
					getConversationChatMessageQueryKey({
						id: conversationId,
					}),
					(messages: Api.AIChatMessage[]) => {
						if (messages?.length > 0 && messages[messages.length - 1].role === Api.AIChatRole.Assistant) {
							const lastAssistantMessage = messages.pop();
							let updatedText = lastAssistantMessage.text;

							updatedText = data.isDelta === false ? messageText : updatedText + messageText;

							return [
								...messages,
								{
									...lastAssistantMessage,
									text: updatedText,
								},
							];
						} else {
							return [
								...(messages || []),
								{
									id: data.message.id,
									role: Api.AIChatRole.Assistant,
									text: messageText,
									creationDate: new Date().toISOString(),
								},
							];
						}
					}
				);
			}
		},
		onError: (error: any) => {
			errorMessages.pushApiError(error);
			logger.logApiError('aiPromptMutation', error);
		},
		onSuccess: () => {
			setHasFetchedFeedback(true);
		},
	});

	// Effect to send analysis message when transcriptions and conversation are ready
	React.useEffect(() => {
		if (
			conversationId &&
			selectedAnalysisType &&
			combinedTranscriptionText &&
			!hasFetchedFeedback &&
			!hasStartedAnalysis &&
			!aiPromptMutation.isLoading
		) {
			setHasStartedAnalysis(true);

			const chatMessages: {
				role: Api.AIChatRole;
				text: string;
				id: string | null;
			}[] = [
				{
					role: Api.AIChatRole.User,
					text: `Multiple Call Transcriptions (${selectedCalls.length} calls):\n${combinedTranscriptionText}`,
					id: null,
				},
			];

			// Only include system message if custom message is provided
			if (customSystemMessage && customSystemMessage.trim()) {
				chatMessages.unshift({
					role: Api.AIChatRole.System,
					text: customSystemMessage,
					id: null,
				});
			}

			aiPromptMutation.mutate({
				assistantName,
				convoId: conversationId,
				chatRequest: {
					chatMessages,
				},
			});
		}
	}, [
		conversationId,
		selectedAnalysisType,
		combinedTranscriptionText,
		hasFetchedFeedback,
		hasStartedAnalysis,
		aiPromptMutation,
		selectedCalls.length,
		customSystemMessage,
		assistantName,
	]);

	// Start analysis for multiple calls
	const startMultiAnalysis = React.useCallback(
		async (analysisType: AnalysisType, systemMessage?: string) => {
			setSelectedAnalysisType(analysisType);
			setCustomSystemMessage(systemMessage || '');
			setIsAnalysisModalOpen(true);
			setConversationMessages([]);
			setAiResponse('');
			setHasFetchedFeedback(false);
			setHasStartedAnalysis(false);
			setCombinedTranscriptionText('');
			setIsNewAnalysisSession(true); // Mark this as a new analysis session

			// Fetch all transcriptions first
			await fetchAllTranscriptions();

			// Create conversation and start analysis
			aiAssistantCreateConversationMutation.mutate({
				assistantName,
				phoneCallId: selectedCalls[0]?.phoneCall?.id, // Use first call's ID for conversation context
				companyId,
				leadServedId,
				source,
				agentTemplateId: agentTemplateId || undefined,
			});
		},
		[
			selectedCalls,
			companyId,
			leadServedId,
			source,
			agentTemplateId,
			assistantName,
			fetchAllTranscriptions,
			aiAssistantCreateConversationMutation,
		]
	);

	const closeAnalysis = React.useCallback(() => {
		setIsAnalysisModalOpen(false);
		setSelectedAnalysisType(null);
		setConversationMessages([]);
		setConversationId(null);
		setAiResponse('');
		setHasFetchedFeedback(false);
		setHasStartedAnalysis(false);
		setCombinedTranscriptionText('');
		setIsNewAnalysisSession(true); // Reset for next session
	}, []);

	// Track when AI response is complete
	const [lastAiResponseCompleted, setLastAiResponseCompleted] = React.useState(false);
	React.useEffect(() => {
		if (!aiPromptMutation.isLoading && aiResponse && hasFetchedFeedback && !lastAiResponseCompleted) {
			setConversationMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
			setLastAiResponseCompleted(true);
		}
	}, [aiPromptMutation.isLoading, aiResponse, hasFetchedFeedback, lastAiResponseCompleted]);

	// Reset tracking when starting a new AI session (but preserve conversation history for follow-ups)
	React.useEffect(() => {
		if (!hasFetchedFeedback && isNewAnalysisSession) {
			setLastAiResponseCompleted(false);
			setConversationMessages([]);
			setIsNewAnalysisSession(false); // Mark that we're no longer in a new session
		} else if (!hasFetchedFeedback && !isNewAnalysisSession) {
			// This is a follow-up question, just reset the completion tracking
			setLastAiResponseCompleted(false);
		}
	}, [hasFetchedFeedback, isNewAnalysisSession]);

	const sendFollowUp = React.useCallback(
		(question: string) => {
			if (!question.trim() || !conversationId) return;

			setConversationMessages(prev => [...prev, { role: 'user', content: question }]);

			// Reset AI response for new streaming response but keep conversation history
			setAiResponse('');
			setHasFetchedFeedback(false);

			aiPromptMutation.mutate({
				assistantName,
				convoId: conversationId,
				chatRequest: {
					chatMessages: [
						{
							role: Api.AIChatRole.User,
							text: question,
							id: null,
						},
					],
				},
			});

			setLastAiResponseCompleted(false);
		},
		[conversationId, aiPromptMutation, assistantName]
	);

	return {
		selectedAnalysisType,
		isAnalysisModalOpen,
		startMultiAnalysis,
		closeAnalysis,
		aiResponse,
		isLoading: aiAssistantCreateConversationMutation.isPending || aiPromptMutation.isLoading || isLoadingTranscriptions,
		conversationMessages,
		conversationId,
		sendFollowUp,
		lastAiResponseCompleted,
		selectedCallCount: selectedCalls.length,
	};
};
